import { PORTAL_SMS_DISCLOSURE, textMessagePolicyHrefs } from '@supply-mover-2-0/shared';
import type { TextMessagePolicyHrefModel } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { usePortalSiteStore } from '../stores/portalSiteStore';

interface TextMessageDisclosureProps {
	/** Classes for the line itself (size, color, spacing) at each call site. */
	className?: string;
}

/**
 * PD-17137 — the texting disclosure, with links to the brand's texting terms
 * and privacy policy, shown wherever a customer gives a mobile number for
 * texts: the sign-in screen when a code will be texted, and every Text box.
 * Carriers check each of these screens for the frequency, rates, HELP and STOP
 * wording and the two links; HF Foods' campaign was rejected (Twilio 30909)
 * when they were missing.
 *
 * The links go to the brand's own policies when its portal site sets them,
 * otherwise to this storefront's Text Messages page. A page link opens in the
 * same tab; another site's opens in a new one so the visitor keeps their place.
 */
export function TextMessageDisclosure({
	className,
}: TextMessageDisclosureProps): JSX.Element {
	const { t } = useTranslation();
	const policy = usePortalSiteStore((s) => s.site?.textMessagePolicy ?? null);
	const hrefs = textMessagePolicyHrefs(policy);

	return (
		<p className={className}>
			{t(PORTAL_SMS_DISCLOSURE)}{' '}
			<PolicyLink href={hrefs.terms} label={t('Terms')} />
			{' '}
			{t('·')}{' '}
			<PolicyLink href={hrefs.privacy} label={t('Privacy')} />
		</p>
	);
}

function PolicyLink({
	href,
	label,
}: {
	href: TextMessagePolicyHrefModel;
	label: string;
}): JSX.Element {
	return (
		<a
			href={href.href}
			{...(href.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
			className="underline hover:text-portal-accent"
		>
			{label}
		</a>
	);
}
